import { api } from '@goaltree/backend/convex/_generated/api'
import {
  COACH_STYLES,
  coachDisplayName,
  coachNameOverride,
  isFallingBehind,
  resolveDynamicCoachStyle,
} from '@goaltree/backend/convex/lib/coach'
import { cn } from '@goaltree/ui/lib/utils'
import { useQuery } from 'convex/react'
import { X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUpdatePreferences } from '@/hooks/use-update-preferences'

type Prefs = {
  coachVoice?: string
  coachName?: string
  coachStyle?: string
  lifestyle?: string
  onboardingCompletedAt?: number
  coachTonePromptAt?: number
}

// One-time ask (per user, stored server-side) whether to keep the adaptive coach
// tone: the first time it turns strict, or after the first weekly review.
export function CoachTonePrompt({ className }: { className?: string }) {
  const { t } = useTranslation()
  const prefs = useQuery(api.preferences.getMyPreferences) as Prefs | undefined
  const updatePrefs = useUpdatePreferences()
  const [answered, setAnswered] = useState(false)

  const onboardedAt = prefs?.onboardingCompletedAt
  const eligible =
    !answered &&
    !!prefs &&
    !prefs.coachStyle &&
    !prefs.coachTonePromptAt &&
    !!onboardedAt &&
    new Date(onboardedAt).toDateString() !== new Date().toDateString()
  const goals = useQuery(api.nodes.getUserGoals, eligible ? {} : 'skip')
  const streakInfo = useQuery(api.nodes.getUserStreakInfo, eligible ? {} : 'skip')
  const reviews = useQuery(api.weeklyReview.getReviewHistory, eligible ? {} : 'skip')

  const strict =
    !!goals &&
    !!streakInfo &&
    resolveDynamicCoachStyle({
      lifestyle: prefs?.lifestyle,
      behind: isFallingBehind(
        goals
          .filter((g) => !g.isInbox && g.status !== 'done')
          .map((g) => ({ progress: g.progress, remaining: g.remainingCount })),
        streakInfo.streak,
      ),
    }) === 'strict'
  const trigger = !eligible ? null : strict ? 'strict' : reviews?.length ? 'review' : null
  if (!trigger) return null

  const coach = coachDisplayName(prefs?.coachVoice, coachNameOverride(prefs))
  const answer = (coachStyle?: string) => {
    setAnswered(true)
    void updatePrefs({ coachTonePromptAt: Date.now(), ...(coachStyle && { coachStyle }) }).catch(
      () => {},
    )
  }
  const chip = 'cursor-pointer rounded-full border px-3 py-1 text-xs font-medium transition-colors'

  return (
    <div
      className={cn('relative rounded-2xl border border-border/60 bg-card/40 p-3 pr-9', className)}
    >
      <p className="text-sm text-foreground/80">{t(`coach.tonePrompt.${trigger}`, { coach })}</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => answer()}
          className={cn(chip, 'border-primary/70 bg-primary/10 text-primary-text')}
        >
          {t('coach.tonePrompt.keep', { tone: t('coach.adaptive') })}
        </button>
        {COACH_STYLES.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => answer(s.id)}
            className={cn(
              chip,
              'border-border/60 text-muted-foreground hover:border-border hover:text-foreground',
            )}
          >
            {t(`coach.style.${s.id}`)}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => answer()}
        aria-label={t('coach.tonePrompt.dismiss')}
        className="absolute top-2.5 right-2.5 cursor-pointer text-muted-foreground transition-colors hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
